import { Button, Spinner } from "@fluentui/react-components";
import { ArrowDownloadRegular } from "@fluentui/react-icons";
import { getAccessTokenUsingMsal, useMsal } from "@skanska/vsa-client-auth";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { apiConfig } from "../../config/AppConfig";
import { loginRequest } from "../../config/MsalConfig";
import { ApiEndpoints } from "../../models/ApiEndpoints";

interface DownloadOriginalDocumentsButtonProps {
  versionIds: string[];
}

const getFilenameFromContentDisposition = (
  contentDisposition: string | null
) => {
  if (!contentDisposition) {
    return "originalfiler.zip";
  }

  const filename = contentDisposition
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.toLowerCase().startsWith("filename="))
    ?.split("=")[1]
    ?.trim();

  return filename?.replace(/^['"]|['"]$/g, "") || "originalfiler.zip";
};

const DownloadOriginalDocumentsButton = ({
  versionIds,
}: DownloadOriginalDocumentsButtonProps) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { instance, inProgress } = useMsal();
  const hasSelectedDocuments = versionIds.length > 0;

  const handleDownloadClick = async () => {
    if (!hasSelectedDocuments) {
      return;
    }

    setLoading(true);

    try {
      const accessToken = await getAccessTokenUsingMsal(
        inProgress,
        instance,
        loginRequest.scopes,
        apiConfig.tenantId
      );

      const response = await fetch(
        `${apiConfig.apiUrl}/${ApiEndpoints.ToolOriginalDownloads}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ versionIds }),
        }
      );

      if (!response.ok) {
        throw new Error(await response.text());
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = getFilenameFromContentDisposition(
        response.headers.get("Content-Disposition")
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download original files", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      icon={loading ? <Spinner size="tiny" /> : <ArrowDownloadRegular />}
      onClick={handleDownloadClick}
      disabled={!hasSelectedDocuments || loading}
    >
      {t("downloadOriginalSelectedDocuments", { count: versionIds.length })}
    </Button>
  );
};

export default DownloadOriginalDocumentsButton;
